import { sha256 } from '@noble/hashes/sha2.js'
import { bytesToHex } from '@noble/hashes/utils.js'
import { EventTemplate, NostrEvent } from '@nostr/tools/wasm'
import * as kinds from '@nostr/tools/kinds'
import { computeBlobSha256, createUploadAuth, mirrorBlob, uploadBlob } from '@nostr/tools/nipb7'
import { naddrEncode, npubEncode } from '@nostr/tools/nip19'
import { getDefaultStore } from 'jotai'
import client from '@/services/client.service'
import { accountAtom, signerAtom } from '@/atoms/session'
import { blossomServersAtom, unwrappedRelayListAtom } from '@/atoms/lists'
import { pubkeyToB36 } from '@/lib/base36'
import { now } from '@/lib/utils'

// 44billion relay hints — appended to the publish target list so the new nsite
// manifest gets indexed across them too.
export const BILLIONAIRE_RELAYS = [
  'wss://relay.44billion.net',
  'wss://relay.ditto.pub',
  'wss://relay.dreamith.to'
] as const

// Suggested nsite gateways. These follow the convention of hosting nsites at
// `<subdomain>.nsite.lol`; the canonical subdomain form per NIP-5A is
// `<50-char-pubkeyB36><dTag>.host`, but most gateways also expose the short
// `<dTag>.host` form.
export const NSITE_GATEWAYS = [
  { label: 'nsite.lol', urlFor: (sub: string) => `https://${sub}.nsite.lol` },
  { label: '44billion.net', urlFor: (sub: string) => `https://44billion.net/${sub}` }
] as const

export type NsiteDeployStage =
  | { stage: 'idle' }
  | { stage: 'fetching'; url: string }
  | { stage: 'injecting' }
  | { stage: 'uploading'; file: string; server: string }
  | { stage: 'mirroring'; server: string; file: string }
  | { stage: 'signing' }
  | { stage: 'publishing'; relay: string; done: number; total: number }
  | { stage: 'done'; eventId: string; naddr: string; pubkey: string }
  | { stage: 'error'; error: string }

export type DeployableFile = {
  // Absolute path on the site, must start with "/".
  path: string
  // Raw content as a Uint8Array — we always work with bytes so hashes are
  // computed deterministically.
  content: Uint8Array
  // MIME type, used as the Content-Type on upload.
  type: string
}

export type DeployNsiteParams = {
  // d tag value (site identifier). 1-13 chars, lowercase, digits and dashes,
  // cannot end with "-". Becomes the subdomain on nsite gateways.
  d: string
  // description tag content (free text).
  description: string
  // The Hallway universe config that will be baked into the deployed
  // index.html — must be JSON-serializable.
  universe: Record<string, unknown>
  // Optional human-readable site title.
  title?: string
  // Optional source archive URL. When provided, index.html is fetched from
  // <sourceURL>/index.html and used as the only deployable file. The actual
  // list-of-all-files fetching is left as a future extension; today this only
  // pulls index.html.
  sourceURL?: string
  // Optional pre-built files to deploy in addition to (or instead of) the
  // fetched index.html. Use this when the caller already knows the file list.
  files?: DeployableFile[]
  // Additional blossom servers beyond the ones in the user's BUD-03 server
  // list. Common defaults are the same servers used in
  // window.hallway.universe.defaultBlossomServers.
  extraBlossomServers?: string[]
}

export type DeployNsiteResult = {
  eventId: string
  naddr: string
  pubkey: string
  pathTags: [string, string, string][]
  blossomUrls: string[]
}

// Inject the universe JSON into a fetched HTML document, mirroring what
// deployer/deploy.go's injectUniverseScript does. We insert a <script type="application/json"
// id="universe"> block plus the runtime boot script that turns it into
// window.hallway.universe, placed right before the first existing <script> tag,
// or right before </head> if no <script> tag exists.
export function injectUniverseScript(
  html: Uint8Array,
  universe: Record<string, unknown>
): Uint8Array {
  const universeJSON = JSON.stringify(universe)
  const tag =
    `<script type="application/json" id="universe">${universeJSON}</script>\n` +
    `<script>window.hallway = {universe: JSON.parse(document.getElementById('universe').textContent)};</script>\n`

  const text = new TextDecoder().decode(html)
  let inserted = false
  let out = text

  // Try to insert before the first existing <script ...> tag.
  const scriptIdx = text.indexOf('<script')
  if (scriptIdx >= 0) {
    out = text.slice(0, scriptIdx) + tag + text.slice(scriptIdx)
    inserted = true
  } else {
    const headCloseIdx = text.indexOf('</head>')
    if (headCloseIdx >= 0) {
      out = text.slice(0, headCloseIdx) + tag + text.slice(headCloseIdx)
      inserted = true
    }
  }

  if (!inserted) return html
  return new TextEncoder().encode(out)
}

function validateDTag(d: string): void {
  if (!/^[a-z0-9-]{1,13}$/.test(d)) {
    throw new Error('nsite id must be 1-13 chars of lowercase letters, digits and hyphens')
  }
  if (d.endsWith('-')) {
    throw new Error('nsite id must not end with "-"')
  }
}

// Trivial source fetcher: pulls index.html only. The "list of every asset in
// the dist" feature is intentionally left as a future extension.
async function fetchIndexHtmlFromSource(sourceURL: string): Promise<DeployableFile> {
  const base = sourceURL.replace(/\/+$/, '')
  const url = base + '/index.html'
  const resp = await fetch(url)
  if (!resp.ok) {
    throw new Error(`failed to fetch ${url}: ${resp.status} ${resp.statusText}`)
  }
  const buf = new Uint8Array(await resp.arrayBuffer())
  return {
    path: '/index.html',
    content: buf,
    type: resp.headers.get('content-type') || 'text/html; charset=utf-8'
  }
}

// Sort + dedupe the servers we will use, dropping entries that fail URL
// parsing so we don't crash the upload loop with a malformed value.
function normalizeBlossomServers(input: string[]): string[] {
  const out: string[] = []
  const seen = new Set<string>()
  for (const raw of input) {
    if (!raw) continue
    try {
      const u = new URL(raw)
      const host = u.host.toLowerCase()
      if (seen.has(host)) continue
      seen.add(host)
      out.push(u.toString().replace(/\/+$/, '') + '/')
    } catch {
      // skip
    }
  }
  return out
}

async function uploadFile(
  file: DeployableFile,
  servers: string[],
  signEvent: (t: EventTemplate) => Promise<NostrEvent>,
  onProgress?: (s: NsiteDeployStage) => void
): Promise<{ hash: string; urls: string[] }> {
  if (servers.length === 0) {
    throw new Error(
      'no blossom servers configured — set up at least one in your BUD-03 server list or in the form'
    )
  }

  const blob = new Blob([file.content], { type: file.type })
  const hash = await computeBlobSha256(blob)
  const urls: string[] = []
  let lastErr: unknown = null

  for (const server of servers) {
    onProgress?.({ stage: 'uploading', file: file.path, server })
    try {
      const desc = await uploadBlob(server, blob, {
        auth: true,
        onAuth: (_srv: string, sha: string) => createUploadAuth(signEvent, sha, { type: 'upload' })
      })
      urls.push(desc.url)
      // Mirror to the remaining servers in the background; mirrorBlob prefers
      // URL-based copies and falls back to a re-upload if the server doesn't
      // have the blob yet.
      for (const mirrorServer of servers) {
        if (mirrorServer === server) continue
        onProgress?.({ stage: 'mirroring', server: mirrorServer, file: file.path })
        try {
          await mirrorBlob(mirrorServer, desc, {
            auth: true,
            onAuth: (_srv: string, sha: string) =>
              createUploadAuth(signEvent, sha, { type: 'upload' })
          })
          urls.push(`${mirrorServer.replace(/\/+$/, '')}/${desc.sha256}`)
        } catch {
          // mirror failure is non-fatal; user can still serve from the
          // primary server.
        }
      }
      return { hash, urls }
    } catch (e) {
      lastErr = e
      // try next server
    }
  }

  throw new Error(
    `failed to upload ${file.path} to any blossom server${lastErr ? ': ' + (lastErr as Error).message : ''}`
  )
}

// Aggregate hash per NIP-5A: sha256 of `<hash> <path>\n` lines, sorted
// lexicographically. Returns lowercase hex.
async function computeAggregateHash(paths: { path: string; hash: string }[]): Promise<string> {
  const lines = paths
    .map((p) => `${p.hash} ${p.path}\n`)
    .sort((a, b) => (a < b ? -1 : a > b ? 1 : 0))
    .join('')
  return bytesToHex(sha256(new TextEncoder().encode(lines)))
}

export async function deployNsite(
  params: DeployNsiteParams,
  onProgress?: (s: NsiteDeployStage) => void
): Promise<DeployNsiteResult> {
  validateDTag(params.d)

  const jotaiStore = getDefaultStore()
  const account = jotaiStore.get(accountAtom)
  const signer = await jotaiStore.get(signerAtom)
  if (!account || !signer) throw new Error('You need to be logged in to publish an nsite')
  if (account.signerType === 'npub')
    throw new Error('Nostr nsite publishing requires a private key signer')

  const signEvent = signer.signEvent.bind(signer) as (t: EventTemplate) => Promise<NostrEvent>

  // ---- 1. build the file list -------------------------------------------------
  let files: DeployableFile[] = []
  if (params.files && params.files.length > 0) {
    files = params.files
  } else if (params.sourceURL) {
    onProgress?.({ stage: 'fetching', url: params.sourceURL + '/index.html' })
    files = [await fetchIndexHtmlFromSource(params.sourceURL)]
  } else {
    throw new Error('no source URL or files provided — set a source URL in the form')
  }

  // ---- 2. inject the universe JSON into index.html ----------------------------
  onProgress?.({ stage: 'injecting' })
  files = files.map((f) =>
    f.path === '/index.html'
      ? { ...f, content: injectUniverseScript(f.content, params.universe) }
      : f
  )

  // ---- 3. upload to blossom ---------------------------------------------------
  const userServers = await jotaiStore.get(blossomServersAtom)
  const servers = normalizeBlossomServers([
    ...userServers,
    ...(params.extraBlossomServers ?? []),
    ...(window.hallway.universe.defaultBlossomServers ?? [])
  ])

  const uploaded: { path: string; hash: string; urls: string[] }[] = []
  for (const file of files) {
    const { hash, urls } = await uploadFile(file, servers, signEvent, onProgress)
    uploaded.push({ path: file.path, hash, urls })
  }

  // ---- 4. assemble the nsite manifest ----------------------------------------
  const pathTags: [string, string, string][] = uploaded.map((u) => ['path', u.path, u.hash])
  const aggregate = await computeAggregateHash(uploaded)

  const tags: string[][] = []
  tags.push(['d', params.d])
  for (const p of pathTags) tags.push(p)
  tags.push(['x', aggregate, 'aggregate'])
  if (params.title) tags.push(['title', params.title])
  if (params.description) tags.push(['description', params.description])
  if (params.sourceURL) tags.push(['source', params.sourceURL])
  // Dedupe server tags by hostname and include every blossom server we used.
  const seen = new Set<string>()
  for (const url of uploaded.flatMap((u) => u.urls)) {
    try {
      const h = new URL(url).host
      if (!seen.has(h)) {
        seen.add(h)
        tags.push(['server', `${new URL(url).protocol}//${h}`])
      }
    } catch {
      // skip
    }
  }

  // ---- 5. sign + publish -----------------------------------------------------
  onProgress?.({ stage: 'signing' })
  const draft: EventTemplate = {
    kind: kinds.NsiteNamed,
    content: '',
    created_at: now(),
    tags
  }
  const event = await signEvent(draft)

  // Publish to user's write relays + bigRelays + 44billion relays. We pass
  // the explicit list via specifiedRelayUrls so client.determineTargetRelays
  // doesn't second-guess our selection.
  const relayList = (await jotaiStore.get(unwrappedRelayListAtom)) ?? []

  const writeUrls = relayList.filter((r) => r.write).map((r) => r.url)
  const bigUrls = (window.hallway.universe.bigRelayUrls as string[]) ?? []
  const targetRelays = Array.from(
    new Set([...writeUrls, ...bigUrls, ...BILLIONAIRE_RELAYS].map((s) => s.trim()).filter(Boolean))
  )

  if (targetRelays.length === 0) {
    throw new Error('no publish targets — log in and configure your write relays')
  }

  // Publish to all target relays in parallel. client.publishEvent fans out to
  // each URL via pool.ensureRelay(). We surface a single progress event so the
  // UI shows the active relay set rather than a misleading per-relay count.
  if (targetRelays[0]) {
    onProgress?.({
      stage: 'publishing',
      relay: targetRelays[0],
      done: targetRelays.length,
      total: targetRelays.length
    })
  }
  try {
    await client.publishEvent(targetRelays, event)
  } catch {
    // client.publishEvent rejects when no relay accepted; for the nsite flow
    // we still want to surface the event id so the user knows the event was
    // signed and may have been partially published. Re-throw to let the
    // caller decide.
    throw new Error('publish failed on all relays — check your connections')
  }

  const pubkey = account.pubkey
  const naddr = naddrEncode({
    pubkey,
    kind: kinds.NsiteNamed,
    identifier: params.d,
    relays: targetRelays.slice(0, 3)
  })

  onProgress?.({
    stage: 'done',
    eventId: event.id,
    naddr,
    pubkey
  })

  return {
    eventId: event.id,
    naddr,
    pubkey,
    pathTags,
    blossomUrls: uploaded.flatMap((u) => u.urls)
  }
}

// Build the canonical gateway URL list for the given pubkey + d tag, used by
// the UI to surface the freshly published site.
export function gatewayUrlsFor(pubkeyHex: string, d: string): { label: string; url: string }[] {
  const sub = `${pubkeyToB36(pubkeyHex)}${d}`
  return [
    { label: 'nsite.lol (full)', url: `https://${sub}.nsite.lol` },
    { label: 'nsite.lol', url: `https://${d}.nsite.lol` },
    { label: '44billion.net', url: `https://44billion.net/${d}` },
    { label: 'npub', url: `https://njump.me/${npubEncode(pubkeyHex)}` }
  ]
}
